import { StakeholderRole } from '../../types/stakeholder.types';

type StakeholderActionsProps = {
  isAccessRevoked?: boolean;
  isActive?: boolean;
  isInvited?: boolean;
  isTerminated?: boolean;
  role: StakeholderRole;
};

type StakeholderPermission = {
  cancelInvitation?: boolean;
  canDelete?: boolean;
  canEditTerminate?: boolean;
  canInvite?: boolean;
  canTerminate?: boolean;
  grantAccess?: boolean;
  makeContributor?: boolean;
  resendInvitation?: boolean;
  revokeAccess?: boolean;
  revokeContributor?: boolean;
};

type Status =
  | 'ACTIVE'
  | 'INACTIVE'
  | 'INACTIVE_ACCESS_REVOKED'
  | 'INACTIVE_INVITED'
  | 'INACTIVE_NOT_INVITED'
  | 'TERMINATED'
  | 'TERMINATED_ACCESS_REVOKED';

type RolePermissions = {
  [status in Status]?: StakeholderPermission;
};

type Permissions = {
  [role in StakeholderRole]: RolePermissions;
};

const stakeholderPermissions: Permissions = {
  [StakeholderRole.STAKEHOLDER]: {
    TERMINATED: {
      canEditTerminate: true,
      canDelete: true,
      revokeAccess: true,
    },
    TERMINATED_ACCESS_REVOKED: {
      grantAccess: true,
      canDelete: true,
    },
    INACTIVE_NOT_INVITED: {
      makeContributor: true,
      canDelete: true,
      canInvite: true,
      canTerminate: true,
    },
    INACTIVE_INVITED: {
      makeContributor: true,
      resendInvitation: true,
      cancelInvitation: true,
      canDelete: true,
      canTerminate: true,
    },
    INACTIVE_ACCESS_REVOKED: {
      grantAccess: true,
      canTerminate: true,
      canDelete: true,
    },
    ACTIVE: {
      revokeAccess: true,
      makeContributor: true,
      canTerminate: true,
      canDelete: true,
    },
  },
  [StakeholderRole.CONTRIBUTOR]: {
    TERMINATED: {
      canEditTerminate: true,
      canDelete: true,
    },
    ACTIVE: {
      canTerminate: true,
      revokeContributor: true,
      revokeAccess: true,
      canDelete: true,
    },
    INACTIVE_INVITED: {
      revokeContributor: true,
      resendInvitation: true,
      cancelInvitation: true,
      canDelete: true,
      canTerminate: true,
    },
    INACTIVE_ACCESS_REVOKED: {
      revokeContributor: true,
      grantAccess: true,
      canTerminate: true,
      canDelete: true,
    },
    INACTIVE_NOT_INVITED: {
      revokeContributor: true,
      canDelete: true,
      canInvite: true,
      canTerminate: true,
    },
  },
  [StakeholderRole.ADMIN]: {
    TERMINATED: {
      canEditTerminate: true,
      canDelete: true,
    },
    ACTIVE: {
      canTerminate: true,
    },
  },
};

export const getStakeholderCommonActions = ({
  role,
  isAccessRevoked = false,
  isActive = false,
  isInvited = false,
  isTerminated = false,
}: StakeholderActionsProps): StakeholderPermission => {
  const defaultPermissions: StakeholderPermission = {
    cancelInvitation: false,
    canDelete: false,
    canInvite: false,
    canTerminate: false,
    grantAccess: false,
    makeContributor: false,
    resendInvitation: false,
    revokeAccess: false,
    revokeContributor: false,
    canEditTerminate: false,
  };

  switch (role) {
    case StakeholderRole.ADMIN:
      if (isTerminated)
        return {
          ...defaultPermissions,
          ...stakeholderPermissions.admin.TERMINATED,
        };

      return {
        ...defaultPermissions,
        ...stakeholderPermissions.admin.ACTIVE,
      };
    case StakeholderRole.CONTRIBUTOR:
      if (isTerminated)
        return {
          ...defaultPermissions,
          ...stakeholderPermissions.contributor.TERMINATED,
        };

      if (isActive)
        return {
          ...defaultPermissions,
          ...stakeholderPermissions.contributor.ACTIVE,
        };

      if (isAccessRevoked)
        return {
          ...defaultPermissions,
          ...stakeholderPermissions.contributor.INACTIVE_ACCESS_REVOKED,
        };

      if (isInvited)
        return {
          ...defaultPermissions,
          ...stakeholderPermissions.contributor.INACTIVE_INVITED,
        };

      return {
        ...defaultPermissions,
        ...stakeholderPermissions.contributor.INACTIVE_NOT_INVITED,
      };
    case StakeholderRole.STAKEHOLDER:
      if (isTerminated)
        return {
          ...defaultPermissions,
          ...stakeholderPermissions.stakeholder.TERMINATED,
        };

      if (isTerminated && isAccessRevoked)
        return {
          ...defaultPermissions,
          ...stakeholderPermissions.stakeholder.TERMINATED_ACCESS_REVOKED,
        };

      if (isActive)
        return {
          ...defaultPermissions,
          ...stakeholderPermissions.stakeholder.ACTIVE,
        };

      if (isAccessRevoked)
        return {
          ...defaultPermissions,
          ...stakeholderPermissions.stakeholder.INACTIVE_ACCESS_REVOKED,
        };

      if (isInvited)
        return {
          ...defaultPermissions,
          ...stakeholderPermissions.stakeholder.INACTIVE_INVITED,
        };

      return {
        ...defaultPermissions,
        ...stakeholderPermissions.stakeholder.INACTIVE_NOT_INVITED,
      };
  }
};
